import {
  useState,
  createContext,
  useContext,
  ReactNode,
  memo,
  useRef,
  useEffect,
} from 'react';
import { toDateString } from '../../functions/src/util/dates/Dates';
import { toLocalMidnightDate } from '../../functions/src/util/date/paramsUtc';

export const MS_PER_DAY = 1000 * 60 * 60 * 24;
export const EARLIEST_START_DATE = new Date('01/01/1970');
export const LATEST_END_DATE = new Date('01/01/2100');

export type EventKeyed = { key: string; Element: ReactNode };
export type DateString = string;
export type FetchDirection = 'before' | 'after';

export type FetchResult = {
  count: number;
  eventsNew: Record<DateString, EventKeyed[]>;
};

export type EventsLazyContextType = {
  events: Record<DateString, EventKeyed[]>;
  fetchMore: (
    startDate: DateString,
    endDate?: DateString,
  ) => Promise<FetchResult>;
  fetchMoreWithoutSet: (
    startDate: DateString,
    endDate?: DateString,
  ) => Promise<FetchResult>;
  fetchAtLeast: (
    startDate: DateString,
    endDate: DateString,
    direction: FetchDirection,
    maxAttempts?: number,
  ) => Promise<FetchResult>;
  fetchAtLeastWithoutSet: (
    startDate: DateString,
    endDate: DateString,
    direction: FetchDirection,
    maxAttempts?: number,
  ) => Promise<FetchResult>;
  clearEvents: (startDate?: DateString, endDate?: DateString) => void;
  push: (events: Record<DateString, EventKeyed[]>) => void;
};

const EventsLazyContext = createContext<EventsLazyContextType>(
  null as unknown as EventsLazyContextType,
);

export const useEventsLazy = (): EventsLazyContextType => {
  const context = useContext(EventsLazyContext);
  if (context === undefined) {
    throw new Error(
      'EventsLazyContext must be used within an EventsLazyProvider',
    );
  }
  return context;
};

export type EventKeyedsLazyProviderProps = {
  children: ReactNode;
  eventsInitial?: Record<DateString, EventKeyed[]>;
  fetchEvents: (
    startDate: DateString,
    endDate: DateString,
  ) => Promise<Record<DateString, EventKeyed[]>>;
};

const EventsLazyProviderUnmemoized = ({
  children,
  eventsInitial = {},
  fetchEvents,
}: EventKeyedsLazyProviderProps): JSX.Element => {
  const [events, setEvents] =
    useState<Record<DateString, EventKeyed[]>>(eventsInitial);
  const cachedEventsRef = useRef<Record<DateString, EventKeyed[]>>({});

  useEffect(() => {
    setEvents(eventsInitial);
  }, [eventsInitial]);
  const clearEvents = (startDate?: DateString, endDate?: DateString) => {
    if (startDate && endDate) {
      const start = toLocalMidnightDate(startDate);
      const end = toLocalMidnightDate(endDate);
      const newEvents: Record<DateString, EventKeyed[]> = { ...events };
      const deletedEvents: Record<DateString, EventKeyed[]> = {};

      for (
        const date = new Date(start);
        date <= end;
        date.setDate(date.getDate() + 1)
      ) {
        const dateString = toDateString(date);
        if (newEvents[String(dateString)]) {
          deletedEvents[String(dateString)] = newEvents[String(dateString)];
          delete newEvents[String(dateString)];
        }
      }

      cachedEventsRef.current = {
        ...cachedEventsRef.current,
        ...deletedEvents,
      };
      setEvents(newEvents);
    } else {
      cachedEventsRef.current = { ...cachedEventsRef.current, ...events };
      setEvents({});
    }
  };

  const separateDateCategories = (dates: DateString[]) => {
    const firestoreDates: DateString[] = [];
    const cacheDates: DateString[] = [];
    const existingDates: DateString[] = [];

    dates.forEach((dateString) => {
      if (events[String(dateString)]) {
        existingDates.push(dateString);
      } else if (cachedEventsRef.current[String(dateString)]) {
        cacheDates.push(dateString);
      } else {
        firestoreDates.push(dateString);
      }
    });

    return { existingDates, cacheDates, firestoreDates };
  };

  const extractDateStrings = (start: Date, end: Date) => {
    const dates: DateString[] = [];
    for (const date = start; date <= end; date.setDate(date.getDate() + 1)) {
      dates.push(toDateString(date));
    }
    return dates;
  };

  const fetchFromFirestore = async (firestoreDates: DateString[]) => {
    const fetchPromises = firestoreDates.map((dateString) => {
      return fetchEvents(dateString, dateString);
    });
    return await Promise.all(fetchPromises);
  };

  const fetchFromCache = (cacheDates: DateString[]) => {
    return cacheDates.reduce((acc, current) => {
      acc[String(current)] = cachedEventsRef.current[String(current)];
      return acc;
    }, {} as Record<DateString, EventKeyed[]>);
  };

  const fetchExistingEvents = (existingDates: DateString[]) => {
    return existingDates.reduce((acc, curr) => {
      acc[String(curr)] = events[String(curr)];
      return acc;
    }, {} as Record<DateString, EventKeyed[]>);
  };

  const combineEvents = (
    fetchedEventsFirestore: Record<DateString, EventKeyed[]>[],
    fetchedEventsCache: Record<DateString, EventKeyed[]>,
    eventsExisting: Record<DateString, EventKeyed[]>,
  ) => {
    return fetchedEventsFirestore.reduce(
      (acc, curr) => {
        return { ...acc, ...curr };
      },
      {
        ...eventsExisting,
        ...fetchedEventsCache,
      },
    );
  };

  const sortEvents = (eventsUnsorted: Record<DateString, EventKeyed[]>) => {
    return Object.keys(eventsUnsorted)
      .sort()
      .reduce((sorted, key) => {
        sorted[String(key)] = eventsUnsorted[String(key)];
        return sorted;
      }, {} as Record<DateString, EventKeyed[]>);
  };

  const countNonEmptyFetchedEvents = (
    fetchedEventsFirestore: Record<DateString, EventKeyed[]>[],
  ) => {
    return fetchedEventsFirestore.reduce((acc, current) => {
      return current && Object.keys(current).length > 0 ? acc + 1 : acc;
    }, 0);
  };

  const push = (eventsAdditional: Record<DateString, EventKeyed[]>) => {
    setEvents((prevEvents) => {
      const combinedEvents = { ...prevEvents, ...eventsAdditional };
      return sortEvents(combinedEvents);
    });
  };

  const fetchMoreWithoutSet = async (
    startDate: DateString,
    endDate: DateString = startDate,
  ) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const dates = extractDateStrings(start, end);
    const { existingDates, cacheDates, firestoreDates } =
      separateDateCategories(dates);

    const fetchedEventsFirestore = await fetchFromFirestore(firestoreDates);
    const fetchedEventsCache = fetchFromCache(cacheDates);
    const eventsExisting = fetchExistingEvents(existingDates);

    const eventsNew = combineEvents(
      fetchedEventsFirestore,
      fetchedEventsCache,
      eventsExisting,
    );
    const sortedEvents = sortEvents(eventsNew);

    const nonEmptyFetchedEventsCount = countNonEmptyFetchedEvents(
      fetchedEventsFirestore,
    );
    const count =
      nonEmptyFetchedEventsCount + Object.keys(fetchedEventsCache).length;

    return {
      count,
      eventsNew: sortedEvents,
    };
  };

  const fetchMore = async (
    startDate: DateString,
    endDate: DateString = startDate,
  ) => {
    const { count, eventsNew } = await fetchMoreWithoutSet(startDate, endDate);
    push(eventsNew);

    return {
      count,
      eventsNew: sortEvents(eventsNew),
    };
  };

  const fetchAtLeastWithoutSet = async (
    startDate: DateString,
    endDate: DateString,
    direction: FetchDirection,
    maxAttempts = 10,
  ) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const numDates = (end.getTime() - start.getTime()) / MS_PER_DAY;
    const multiplier = direction === 'after' ? 1 : -1;

    const fetchRecursively = async (
      attempts: number,
      count: number,
      eventsNew: Record<string, EventKeyed[]>,
    ) => {
      if (count >= numDates || attempts >= maxAttempts) {
        return { count, eventsNew };
      }

      start.setDate(start.getDate() + multiplier * (numDates + 1));
      end.setDate(end.getDate() + multiplier * (numDates + 1));

      const { count: countNext, eventsNew: eventsNewNext } =
        await fetchMoreWithoutSet(toDateString(start), toDateString(end));

      return fetchRecursively(attempts + 1, count + countNext, {
        ...eventsNew,
        ...eventsNewNext,
      });
    };

    const { count: countFirst, eventsNew: eventsNewFirst } =
      await fetchMoreWithoutSet(startDate, endDate);

    return fetchRecursively(1, countFirst, eventsNewFirst);
  };

  const fetchAtLeast = async (
    startDate: DateString,
    endDate: DateString,
    direction: FetchDirection,
    maxAttempts = 10,
  ) => {
    const { count, eventsNew } = await fetchAtLeastWithoutSet(
      startDate,
      endDate,
      direction,
      maxAttempts,
    );
    push(eventsNew);

    return {
      count,
      eventsNew: sortEvents(eventsNew),
    };
  };

  return (
    <EventsLazyContext.Provider
      value={{
        events,
        fetchMore,
        fetchMoreWithoutSet,
        fetchAtLeast,
        fetchAtLeastWithoutSet,
        clearEvents,
        push,
      }}
    >
      {children}
    </EventsLazyContext.Provider>
  );
};

export const EventsLazyProvider = memo(EventsLazyProviderUnmemoized);
